import { useMemo } from "react";
import { Platform } from "react-native";

import {
  dotNotationToStyles,
  getAccessibilityPropsByVariation,
} from "@smartrent/tokens";

import { useTheme } from "../../utils/theme/provider";

import type { TextVariation } from "@smartrent/tokens";
import type {
  TextProps as RNTextProps,
  TextStyle,
  ViewStyle,
} from "react-native";
import type { TextType } from "./types";

export const backwardsCompatibleTypeMap: Record<TextType, TextVariation> = {
  body: "regular.body.medium",
  bodySmall: "regular.body.small",
  bodyLarge: "regular.body.large",
  bodySemibold: "semibold.body.medium",
  bodySmallSemibold: "semibold.body.small",
  bodyLargeSemibold: "semibold.body.large",
  title: "semibold.title.one",
  title2: "semibold.title.two",
  title3: "semibold.title.three",
  title4: "semibold.title.four",
  title5: "semibold.title.five",
  title6: "semibold.title.six",
  titleBold: "bold.title.one",
  title2Bold: "bold.title.two",
  title3Bold: "bold.title.three",
  title4Bold: "bold.title.four",
  title5Bold: "bold.title.five",
  title6Bold: "bold.title.six",
  caption: "regular.caption.medium",
  captionSmall: "regular.caption.small",
  captionLarge: "regular.caption.large",
  captionSemibold: "semibold.caption.medium",
  captionSmallSemibold: "semibold.caption.small",
  captionLargeSemibold: "semibold.caption.large",
  button: "button.sentence",
};

export interface useTextPropsOpts
  extends Pick<RNTextProps, "accessibilityRole"> {
  /**
   * @deprecated please use variation
   */
  type?: TextType;
  variation?: TextVariation;
  numberOfLines?: number;
}

export function useTextProps({
  accessibilityRole: parentAccessibilityRole,
  type,
  variation = "regular.body.medium",
  numberOfLines,
}: useTextPropsOpts) {
  const { text } = useTheme();

  const textStyles = useMemo<TextStyle>(() => {
    let textStyles: TextStyle = {};

    if (type) {
      textStyles = dotNotationToStyles(backwardsCompatibleTypeMap[type], text);
    } else {
      textStyles = dotNotationToStyles(variation, text);
    }

    return textStyles;
  }, [text, type, variation]);

  // accessibility
  const { accessibilityRole, accessibilityLevel } = useMemo(() => {
    if (type) {
      return getAccessibilityPropsByVariation(backwardsCompatibleTypeMap[type]);
    }
    return getAccessibilityPropsByVariation(variation);
  }, [type, variation]);

  const safariStyles = useMemo(() => {
    if (Platform.OS !== "web") return undefined;

    const ua = navigator?.userAgent?.toLowerCase();
    if (ua.indexOf("safari") !== -1 && ua.indexOf("chrome") === -1) {
      if (numberOfLines === 1) {
        return {
          whiteSpace: "nowrap",
        } as ViewStyle;
      }

      return {
        WebkitLineClamp: numberOfLines,
      } as ViewStyle;
    }

    return undefined;
  }, [numberOfLines]);

  return {
    accessibilityLevel,
    accessibilityRole: parentAccessibilityRole ?? accessibilityRole,
    safariStyles,
    textStyles,
  };
}
